import { createDebug } from '@shared/debug';
import type { AltenarNavigationParams } from '@shared/hooks';
import { ROUTES } from '@shared/utils';
import { updateUrlParams } from '../utils/sportsbookUrl';
import { type NavEntry, PageType } from './types';

const debug = createDebug({ isEnabled: false });

const BASE_ICON_URL = process.env.IMG_BASE_URL || 'https://www.lottoland.com';

const PAGE_MAPPING: { [key: string]: string } = {
  prelive: 'overview',
  kiron_horses: 'virtual',
  allsports: 'sport',
  'user/settings': 'user',
  esports: 'sport',
  racing: 'racing',
};

const HORSE_RACING_SPORT_ID = 165;
const GREYHOUND_SPORT_ID = 100;
const HORSE_RACING_TYPE = 1;
const GREYHOUND_RACING_TYPE = 2;

const RACING_TYPE_BY_SPORT_ID: Record<number, number> = {
  [HORSE_RACING_SPORT_ID]: HORSE_RACING_TYPE,
  [GREYHOUND_SPORT_ID]: GREYHOUND_RACING_TYPE,
};

const withRacingType = <T extends { page?: string; racingType?: number; sportId?: number }>(params: T): T & { page?: string; racingType?: number } => {
  const racingTypeFromSportId = params.sportId !== undefined ? RACING_TYPE_BY_SPORT_ID[params.sportId] : undefined;
  const racingType = racingTypeFromSportId ?? params.racingType;
  const isRacingPage = params.page === 'racing';

  if (!isRacingPage && racingType === undefined) {
    return params;
  }

  return {
    ...params,
    page: 'racing',
    racingType: racingType ?? HORSE_RACING_TYPE,
  };
};

const fixIconUrl = (iconUrl: string): string => {
  if (iconUrl.includes('https')) {
    return iconUrl;
  }
  return BASE_ICON_URL + iconUrl;
};

const mapPageValue = (pageValue: string): string => {
  return PAGE_MAPPING[pageValue] || pageValue;
};

const filterAndSortEntries = (entries: NavEntry[], isUserLoggedIn: boolean): NavEntry[] => {
  if (!entries || entries.length === 0) return [];

  return entries
    .filter(entry => {
      const visibleForUser = isUserLoggedIn ? true : !entry.hideFromLoggedOut;
      const isEnabledOnAnyDevice = entry.enabledOnDesktop || entry.enabledOnMobile;

      return visibleForUser && isEnabledOnAnyDevice;
    })
    .map(entry => ({
      ...entry,
      icon: {
        ...entry.icon,
        url: fixIconUrl(entry.icon.url),
      },
    }))
    .sort((entryA, entryB) => entryA.desktopPosition - entryB.desktopPosition);
};

const findTargetEntry = (filteredEntries: NavEntry[], pageParam: string): NavEntry | undefined => {
  return filteredEntries.find(entry => {
    if (entry.page?.value) {
      const mappedValue = mapPageValue(entry.page.value);
      return entry.page.value === pageParam || mappedValue === pageParam;
    }
    return entry.contentCategory && pageParam === 'sport' && entry.contentCategory.name === 'sportids';
  });
};

const performNavigation = (entry: NavEntry) => {
  if (typeof window !== 'undefined' && window.altenarWSDK) {
    try {
      if (entry.pageType === PageType.Page && entry.page?.value) {
        const mappedPageValue = mapPageValue(entry.page.value);

        let navigationParams: Partial<AltenarNavigationParams> = {
          page: mappedPageValue,
        };

        if (entry.page.value === 'allsports') {
          navigationParams = {
            page: 'sport',
          };
        } else if (entry.page.value === 'esports' && entry.contentCategory?.name === 'sportids') {
          navigationParams = {
            page: 'sport',
            sportId: parseInt(entry.contentCategory.value),
          };
        }

        window.altenarWSDK.set(withRacingType(navigationParams));
        updateUrlParams({ page: entry.page.value });
      } else if (entry.pageType === PageType.IDs && entry.contentCategory) {
        const { name, value } = entry.contentCategory;
        let navigationParams: Partial<AltenarNavigationParams> = {};

        if (name === 'sportids') {
          navigationParams = {
            page: 'sport',
            sportId: parseInt(value),
          };
        } else if (name === 'catids') {
          navigationParams = {
            page: 'category',
            categoryIds: [parseInt(value)],
          };
        } else if (name === 'champids') {
          navigationParams = {
            page: 'championship',
            championshipIds: [parseInt(value)],
          };
        } else {
          navigationParams = {
            page: 'overview',
          };
        }

        window.altenarWSDK.set(withRacingType(navigationParams));

        const urlParams: { [key: string]: string | string[] | undefined } = {
          page: navigationParams.page || 'sport',
        };

        if (navigationParams.sportId) {
          urlParams.sportId = navigationParams.sportId.toString();
        }
        if (navigationParams.categoryIds && navigationParams.categoryIds.length > 0) {
          urlParams.categoryIds = navigationParams.categoryIds.map(categoryId => categoryId.toString());
        }
        if (navigationParams.championshipIds && navigationParams.championshipIds.length > 0) {
          urlParams.championshipIds = navigationParams.championshipIds.map(championshipId => championshipId.toString());
        }

        updateUrlParams(urlParams);
      }
    } catch (error) {
      debug.forceError('Error during navigation:', error);
      if (entry.pageType === PageType.Page && entry.page?.value) {
        const BASE_URL = ROUTES.SPORTS.ROOT_SPORTSBOOK_PATHNAME;
        window.location.href = `${BASE_URL}?page=${entry.page.value}`;
      }
    }
  } else {
    if (entry.pageType === PageType.Page && entry.page?.value) {
      const BASE_URL = ROUTES.SPORTS.ROOT_SPORTSBOOK_PATHNAME;
      window.location.href = `${BASE_URL}?page=${entry.page.value}`;
    }
  }
};

const performInitialNavigation = (targetEntry: NavEntry, pageParam: string) => {
  try {
    if (targetEntry.pageType === PageType.Page && targetEntry.page?.value) {
      const mappedPageValue = mapPageValue(targetEntry.page.value);

      const navigationParams: Partial<AltenarNavigationParams> = {
        page: mappedPageValue,
      };
      window.altenarWSDK?.set(withRacingType(navigationParams));

      if (pageParam !== mappedPageValue) {
        updateUrlParams({ page: mappedPageValue });
      }
    } else if (targetEntry.pageType === PageType.IDs && targetEntry.contentCategory) {
      const { value } = targetEntry.contentCategory;
      const urlParams = new URLSearchParams(window.location.search);
      const sportId = urlParams.get('sportId');

      const navigationParams: Partial<AltenarNavigationParams> = {
        page: 'sport',
        sportId: sportId ? parseInt(sportId) : parseInt(value),
      };
      window.altenarWSDK?.set(withRacingType(navigationParams));
    }
  } catch (error) {
    debug.forceError('Error during initial navigation:', error);
  }
};

export { filterAndSortEntries, findTargetEntry, fixIconUrl, mapPageValue, PAGE_MAPPING, performInitialNavigation, performNavigation, withRacingType };
